/**
 * QR Codes tab on /records/:unitId (T-044, T-109).
 *
 * Active QR display + history list. Actions reflect the T-109 permanence
 * policy: a retired code can be restored (ordinary swap); ARCHIVING a code and
 * RE-ACTIVATING an archived one are superadmin-only and go through the ceremony
 * dialog (type the unit code + a reason, audited into the unit's history). QR
 * codes are never destroyed — there is no hard-delete.
 *
 * Visual language sticks to design tokens (text-muted-foreground, bg-card) +
 * brand green for the active-row highlight only. No decorative color.
 */

import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import {
  listUnitQrCodes,
  restoreQrCode,
  fetchActiveQrImageUrl,
  fetchQrDownloadUrl,
} from '@/lib/qrCodeApi';
import type { UnitQrCode, QrCodeSource } from '@/types/qr';
import { QrUploadDialog } from './QrUploadDialog';
import { QrCeremonyDialog } from './QrCeremonyDialog';
import { RegenerateQrDialog } from './RegenerateQrDialog';

interface Props {
  unitId: string;
  unitCode: string;
}

const SOURCE_LABEL: Record<QrCodeSource, string> = {
  generated_v1: 'Generated',
  uploaded: 'Uploaded',
  uploaded_arbitrary: 'Uploaded (override)',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(s: string | null | undefined, n = 40): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function QrCodesTab({ unitId, unitCode }: Props) {
  const { isSuperadmin } = useRBAC();
  const [rows, setRows] = useState<UnitQrCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [ceremony, setCeremony] = useState<{ row: UnitQrCode; mode: 'archive' | 'reactivate' } | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setImageUrl(null);
    listUnitQrCodes(unitId)
      .then(async (res) => {
        if (cancelled) return;
        setRows(res.qr_codes);
        if (res.qr_codes.some((r) => r.is_active)) {
          // Endpoint is auth-gated, so we fetch the signed URL with the
          // Bearer token and pass it to <img>. <img src=...> can't carry
          // an Authorization header on its own.
          try {
            const signed = await fetchActiveQrImageUrl(unitId);
            if (!cancelled) setImageUrl(signed);
          } catch {
            if (!cancelled) setError("Couldn't load active QR image.");
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load QR history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, refetchKey]);

  const active = rows.find((r) => r.is_active) || null;
  const history = rows.filter((r) => !r.is_active);

  // T-109: regenerating retires the current permanent sticker, so it is
  // superadmin-only and goes through the typed-phrase ceremony dialog.

  // Restoring a merely-retired (never archived) code is the ordinary swap — no
  // ceremony. Archiving, and re-activating an ARCHIVED code, go through the
  // superadmin ceremony dialog instead.
  async function handleRestore(qrId: string) {
    setPendingId(qrId);
    try {
      await restoreQrCode(unitId, qrId);
      toast.success('QR restored as active.');
      refresh();
    } catch {
      toast.error('Restore failed.');
    } finally {
      setPendingId(null);
    }
  }

  // Download the pre-generated labeled QR (unit code printed under it). The
  // signed URL carries a Content-Disposition attachment + filename, so an
  // anchor click saves it directly — no image is rendered at request time.
  async function handleDownload(format: 'png' | 'svg' | 'pdf', layout?: 'letter' | 'card') {
    setPendingId(`__download_${format}${layout ? `_${layout}` : ''}__`);
    try {
      const url = await fetchQrDownloadUrl(unitId, format, layout);
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error(`Couldn't prepare the ${format.toUpperCase()} download.`);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <CardTitle className="text-base font-semibold">Active QR</CardTitle>
          <div className="flex flex-wrap gap-2">
            {/* Download the pre-generated, print-ready labeled QR. */}
            {active ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload('png')}
                  disabled={pendingId !== null}
                >
                  Download PNG
                </Button>
                {/* Only generated codes carry the vector (labeled) SVG + the two
                    print-ready PDFs; an uploaded code is the operator's own
                    image, PNG only. */}
                {active.source === 'generated_v1' ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload('svg')}
                      disabled={pendingId !== null}
                    >
                      Download SVG
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" disabled={pendingId !== null}>
                          Download PDF
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDownload('pdf', 'letter')}>
                          8.5×11 sheet
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload('pdf', 'card')}>
                          Card (tight square)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : null}
              </>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadOpen(true)}
              disabled={pendingId !== null}
            >
              Upload existing
            </Button>
            {/* T-109: regenerate is superadmin-only + typed-phrase ceremony. */}
            {isSuperadmin ? (
              <Button
                size="sm"
                onClick={() => setRegenerateOpen(true)}
                disabled={pendingId !== null}
              >
                Regenerate
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {active ? (
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={`Active QR for ${unitCode}`}
                  className="w-48 h-auto rounded border bg-card"
                  width={192}
                />
              ) : (
                <div className="w-48 h-48 rounded border bg-card flex items-center justify-center text-xs text-muted-foreground">
                  Loading image…
                </div>
              )}
              <dl className="text-sm space-y-1.5 grid grid-cols-[auto_1fr] gap-x-4">
                <dt className="text-muted-foreground">Payload</dt>
                <dd className="font-mono break-all">{active.qr_payload}</dd>
                <dt className="text-muted-foreground">Source</dt>
                <dd>
                  <Badge variant="secondary">{SOURCE_LABEL[active.source]}</Badge>
                </dd>
                <dt className="text-muted-foreground">Activated</dt>
                <dd>{formatDate(active.activated_at)}</dd>
                {active.label ? (
                  <>
                    <dt className="text-muted-foreground">Label</dt>
                    <dd>{active.label}</dd>
                  </>
                ) : null}
              </dl>
            </div>
          ) : loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No active QR.{' '}
              {isSuperadmin
                ? <>Click <span className="font-medium">Regenerate</span> to create one, or upload an existing sticker.</>
                : <>Upload an existing sticker, or ask a superadmin to regenerate one.</>}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            History {history.length > 0 ? `(${history.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : history.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground">No prior QRs for this unit.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payload</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((row) => {
                  const isArchived = row.archived_at !== null;
                  const busy = pendingId === row.id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        {truncate(row.qr_payload, 40)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{SOURCE_LABEL[row.source]}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell>
                        {isArchived ? (
                          <Badge variant="outline">Archived</Badge>
                        ) : (
                          <Badge variant="outline">Retired</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {/* Retired (never archived): ordinary restore, no ceremony. */}
                        {!isArchived ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestore(row.id)}
                            disabled={busy}
                          >
                            Restore
                          </Button>
                        ) : null}
                        {/* Archived → re-activate via the superadmin ceremony. */}
                        {isArchived && isSuperadmin ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCeremony({ row, mode: 'reactivate' })}
                            disabled={busy}
                          >
                            Re-activate
                          </Button>
                        ) : null}
                        {/* Archive a retired code via the superadmin ceremony. */}
                        {!isArchived && isSuperadmin ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCeremony({ row, mode: 'archive' })}
                            disabled={busy}
                          >
                            Archive
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <QrUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        unitId={unitId}
        unitCode={unitCode}
        onUploaded={refresh}
      />

      {regenerateOpen ? (
        <RegenerateQrDialog
          open
          onOpenChange={(next) => {
            if (!next) setRegenerateOpen(false);
          }}
          unitId={unitId}
          unitCode={unitCode}
          onDone={() => {
            setRegenerateOpen(false);
            refresh();
          }}
        />
      ) : null}

      {ceremony ? (
        <QrCeremonyDialog
          open
          onOpenChange={(next) => {
            if (!next) setCeremony(null);
          }}
          unitId={unitId}
          unitCode={unitCode}
          qrCode={ceremony.row}
          mode={ceremony.mode}
          onDone={() => {
            setCeremony(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
